import React, { useState, useEffect } from 'react';
import { isDismissed, dismissForever, clearStep } from '../utils/walkthroughState';

const TOOLTIP_W = 300;
const GAP = 12;

export const DontShowLink = () => {
  const handleClick = (e) => {
    e.preventDefault();
    dismissForever();
    window.dispatchEvent(new CustomEvent('demo-wt-dismiss'));
  };
  return (
    <>
      <br /><br />
      <a href="#" onClick={handleClick} style={{ fontSize: '0.8em', color: '#94a3b8', textDecoration: 'underline' }}>
        Don't show this again
      </a>
    </>
  );
};

function getPos(target, placement) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!target || target === 'body' || placement === 'center') {
    return { left: Math.round(vw / 2 - TOOLTIP_W / 2), top: Math.round(vh / 2 - 100) };
  }

  const el = document.querySelector(target);
  if (!el) return { left: Math.round(vw / 2 - TOOLTIP_W / 2), top: Math.round(vh / 2 - 100) };

  const r = el.getBoundingClientRect();
  let left, top;

  switch (placement) {
    case 'left':
      left = r.left - GAP - TOOLTIP_W;
      top = r.top + r.height / 2 - 80;
      break;
    case 'right':
      left = r.right + GAP;
      top = r.top + r.height / 2 - 80;
      break;
    case 'top':
      left = r.left + r.width / 2 - TOOLTIP_W / 2;
      top = r.top - GAP - 170;
      break;
    default: // bottom
      left = r.left + r.width / 2 - TOOLTIP_W / 2;
      top = r.bottom + GAP;
  }

  return {
    left: Math.max(8, Math.min(left, vw - TOOLTIP_W - 8)),
    top: Math.max(8, Math.min(top, vh - 220)),
  };
}

const btnStyle = (bg, color) => ({
  background: bg, color, border: 'none', borderRadius: 5,
  padding: '6px 12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
});

const DemoWalkthrough = ({ enabled, steps, delay = 0 }) => {
  const [run, setRun] = useState(false);
  const [idx, setIdx] = useState(0);
  const [pos, setPos] = useState({ left: -9999, top: 0 });

  // Start when enabled flips true.
  useEffect(() => {
    if (!enabled || isDismissed() || !steps?.length) return;
    clearStep();
    const t = setTimeout(() => { setIdx(0); setRun(true); }, delay);
    return () => { clearTimeout(t); setRun(false); };
  }, [enabled, delay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dismiss event from DontShowLink.
  useEffect(() => {
    const h = () => setRun(false);
    window.addEventListener('demo-wt-dismiss', h);
    return () => window.removeEventListener('demo-wt-dismiss', h);
  }, []);

  // Scroll target into view, highlight it, and compute tooltip position.
  useEffect(() => {
    if (!run || !steps?.[idx]) return;
    const step = steps[idx];
    const el = (step.target && step.target !== 'body') ? document.querySelector(step.target) : null;

    if (el) el.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    setPos(getPos(step.target, step.placement));

    if (!el) return;
    const origOutline = el.style.outline;
    const origOffset = el.style.outlineOffset;
    el.style.outline = '2px solid #3B82F6';
    el.style.outlineOffset = '3px';
    return () => { el.style.outline = origOutline; el.style.outlineOffset = origOffset; };
  }, [run, idx]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!run || !steps?.length) return null;

  const step = steps[idx];
  const isLast = idx === steps.length - 1;

  const handleNext = () => {
    if (step.onNext) { setRun(false); step.onNext(); return; }
    if (isLast) { setRun(false); return; }
    setIdx(i => i + 1);
  };

  return (
    <>
      {/* Dark overlay — pointer-events: none so bridge-step clicks pass through */}
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, pointerEvents: 'none' }} />

      {/* Tooltip card */}
      <div style={{
        position: 'fixed', top: pos.top, left: pos.left, width: TOOLTIP_W,
        background: '#fff', borderRadius: 8, boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
        zIndex: 10001, padding: 16, fontFamily: 'sans-serif',
      }}>
        <button
          onClick={() => setRun(false)}
          style={{ position: 'absolute', top: 8, right: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', lineHeight: 1 }}
          aria-label="Close"
        >×</button>

        {step.title && (
          <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 8, paddingRight: 24, color: '#1e293b' }}>
            {step.title}
          </div>
        )}

        <div style={{ fontSize: '0.875rem', color: '#475569', lineHeight: 1.5 }}>
          {step.content}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{idx + 1} / {steps.length}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {idx > 0 && (
              <button onClick={() => setIdx(i => i - 1)} style={btnStyle('#e2e8f0', '#1e293b')}>← Back</button>
            )}
            {!step.hideNextButton && (
              <button onClick={handleNext} style={btnStyle('#3B82F6', '#fff')}>
                {isLast ? 'Done' : 'Next →'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DemoWalkthrough;


